import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, CreditCard, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CardExpirySettingsPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    card_expiry_enabled: true,
    card_expiry_days_before: 30,
    card_expiry_repeat_interval: 7,
    card_expiry_active_only: true,
    card_expiry_email: true,
    card_expiry_whatsapp: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/smtp-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setSettings(prev => ({
          ...prev,
          card_expiry_enabled: response.data.card_expiry_enabled ?? true,
          card_expiry_days_before: response.data.card_expiry_days_before ?? 30,
          card_expiry_repeat_interval: response.data.card_expiry_repeat_interval ?? 7,
          card_expiry_active_only: response.data.card_expiry_active_only ?? true,
          card_expiry_email: response.data.card_expiry_email ?? true,
          card_expiry_whatsapp: response.data.card_expiry_whatsapp ?? false
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/settings/smtp-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('✅ Einstellungen gespeichert!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl">
      {/* Header */}
      <div className="mb-6">
        {!embedded && (
          <Button
            variant="ghost"
            className="mb-4"
            onClick={() => navigate('/communication/settings')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
        )}
        
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CreditCard className="h-6 w-6" />
          Kreditkarten-Ablauf Benachrichtigungen
        </h1>
        <p className="text-gray-600 mt-1">
          Automatische Erinnerungen wenn Kreditkarten bald ablaufen
        </p>
      </div>

      {/* Settings Card */}
      <Card className="border-2 border-amber-200 bg-amber-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            💳 Benachrichtigungs-Einstellungen
          </CardTitle>
          <CardDescription>
            Kunden werden automatisch erinnert, wenn ihre Kreditkarte bald abläuft
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enabled Switch */}
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
            <div>
              <Label className="text-base font-medium">Benachrichtigungen aktiviert</Label>
              <p className="text-sm text-gray-500">Automatische Erinnerungen senden</p>
            </div>
            <Switch
              checked={settings.card_expiry_enabled}
              onCheckedChange={(checked) => setSettings({...settings, card_expiry_enabled: checked})}
            />
          </div>

          {/* Days Before */}
          <div className="p-4 bg-white rounded-lg border">
            <Label className="text-base font-medium">Tage vor Ablauf (Erste Benachrichtigung)</Label>
            <p className="text-sm text-gray-500 mb-2">Wie viele Tage vor dem Ablaufdatum soll die erste Erinnerung gesendet werden?</p>
            <Input
              type="number"
              min="1"
              max="90"
              value={settings.card_expiry_days_before}
              onChange={(e) => setSettings({...settings, card_expiry_days_before: parseInt(e.target.value) || 30})}
              className="w-32"
            />
            <p className="text-xs text-gray-400 mt-1">Standard: 30 Tage</p>
          </div>

          {/* Repeat Interval */}
          <div className="p-4 bg-white rounded-lg border">
            <Label className="text-base font-medium">Wiederholungsintervall (Tage)</Label>
            <p className="text-sm text-gray-500 mb-2">Wie oft soll die Erinnerung wiederholt werden?</p>
            <Input
              type="number"
              min="1"
              max="30"
              value={settings.card_expiry_repeat_interval}
              onChange={(e) => setSettings({...settings, card_expiry_repeat_interval: parseInt(e.target.value) || 7})}
              className="w-32"
            />
            <p className="text-xs text-gray-400 mt-1">Standard: Alle 7 Tage</p>
          </div>

          {/* Active Only */}
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
            <div>
              <Label className="text-base font-medium">Nur aktive Kunden</Label>
              <p className="text-sm text-gray-500">Deaktivierte Kunden erhalten keine Benachrichtigungen</p>
            </div>
            <Switch
              checked={settings.card_expiry_active_only}
              onCheckedChange={(checked) => setSettings({...settings, card_expiry_active_only: checked})}
            />
          </div>

          {/* Communication Channels */}
          <div className="p-4 bg-white rounded-lg border space-y-4">
            <Label className="text-base font-medium">Kommunikationskanäle</Label>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg">📧</span>
                <span>Per Email senden</span>
              </div>
              <Switch
                checked={settings.card_expiry_email}
                onCheckedChange={(checked) => setSettings({...settings, card_expiry_email: checked})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg">💬</span>
                <span>Per WhatsApp senden</span>
              </div>
              <Switch
                checked={settings.card_expiry_whatsapp}
                onCheckedChange={(checked) => setSettings({...settings, card_expiry_whatsapp: checked})}
              />
            </div>
          </div>

          {/* Save Button */}
          <Button
            onClick={saveSettings}
            disabled={saving}
            className="w-full bg-amber-600 hover:bg-amber-700"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Einstellungen speichern
          </Button>
        </CardContent>
      </Card>

      {/* Info Box */}
      <Card className="mt-6 bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <h4 className="font-medium text-blue-900 mb-2">💡 So funktioniert es:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Die Prüfung läuft täglich um 10:00 Uhr automatisch</li>
            <li>• Kunden werden {settings.card_expiry_days_before} Tage vor Ablauf erstmals benachrichtigt</li>
            <li>• Die Erinnerung wird alle {settings.card_expiry_repeat_interval} Tage wiederholt</li>
            <li>• Im Dashboard sehen Sie eine Übersicht aller ablaufenden Karten</li>
            <li>• Sie können Benachrichtigungen auch manuell vom Dashboard aus senden</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default CardExpirySettingsPage;
